import {
  addCompanyReward,
  addDepartmentReward,
  addUserReward,
  getRewards,
} from "../repository/data.repository";
import { createSearchRequest } from "../hooks/useSearchRequest";
import { Reward } from "../repository/data.default";
import { Dayjs } from "dayjs";
import AssignEntityFormDialog from "./AssignEntityFormDialog";

export interface AssignRewardFormDialogProps {
  readonly open?: boolean;
  readonly initialValues?: Reward;
  readonly target?: {
    readonly type: "user";
    readonly uid: string;
  };
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

// Thin wrapper over the shared AssignEntityFormDialog: a reward picker + assignment/expiration dates,
// fanned out to the company/department/user reward-assignment endpoints.
function AssignRewardFormDialog(props: AssignRewardFormDialogProps) {
  return (
    <AssignEntityFormDialog
      open={props.open}
      title="Assign Reward"
      entityField="reward"
      initialEntityValues={
        props.initialValues
          ? {
              reward: {
                display: props.initialValues.name,
                value: props.initialValues.uid,
              },
            }
          : undefined
      }
      target={props.target}
      entityControls={[
        {
          type: "combobox",
          label: "Reward",
          name: "reward",
          request: createSearchRequest(getRewards, "name"),
          rules: [{ required: true, message: "Reward is required" }],
        },
        {
          type: "datepicker",
          label: "Assignment Date",
          name: "assignmentDate",
          size: "w-3/6",
          rules: [{ required: true, message: "Assignment Date is required" }],
        },
        {
          type: "datepicker",
          label: "Expiration Date",
          name: "expirationDate",
          size: "w-3/6",
          rules: [
            ({ getFieldValue }) => ({
              validator(_, expirationDate) {
                const assignmentDate = getFieldValue("assignmentDate");
                if (
                  !expirationDate ||
                  !assignmentDate ||
                  assignmentDate < expirationDate
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    "Expiration date cannot be less than assignment date",
                  ),
                );
              },
            }),
          ],
        },
      ]}
      canSubmit={(v) => !!(v?.reward?.value && v?.assignmentDate)}
      assignFns={{
        company: (companyUid, v) =>
          addCompanyReward({
            assignmentDate: (v.assignmentDate as Dayjs).toDate(),
            expirationDate: (v.expirationDate as Dayjs)?.toDate(),
            rewardUid: v.reward.value,
            companyUid,
          }),
        department: (departmentUid, v) =>
          addDepartmentReward({
            assignmentDate: (v.assignmentDate as Dayjs).toDate(),
            expirationDate: (v.expirationDate as Dayjs)?.toDate(),
            rewardUid: v.reward.value,
            departmentUid,
          }),
        user: (userUid, v) =>
          addUserReward({
            assignmentDate: (v.assignmentDate as Dayjs).toDate(),
            expirationDate: (v.expirationDate as Dayjs)?.toDate(),
            rewardUid: v.reward.value,
            userUid,
          }),
      }}
      onFinish={props.onFinish}
      onCancel={props.onCancel}
    />
  );
}

export default AssignRewardFormDialog;
